import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, poundsFromPence } from "../../lib/api";
import { SuperCard } from "../../components/SuperAdminLayout";
import { Search } from "lucide-react";

const STATUS_BADGE = {
  active: "text-[#86efac] bg-[#0d2818]",
  suspended: "text-[#facc15] bg-[#1a1205]",
  banned: "text-[#fca5a5] bg-[#2a1212]",
};

export default function SuperAttractionsPage() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [stripe, setStripe] = useState("all");
  const [setup, setSetup] = useState("all");
  const [sort, setSort] = useState("newest");
  const [page, setPage] = useState(1);

  const load = () => {
    api.get("/super/attractions", { params: { search: q || undefined, status, stripe, setup, sort, page } })
      .then((r) => setData(r.data));
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, [status, stripe, setup, sort, page]);
  useEffect(() => { const t = setTimeout(() => { setPage(1); load(); }, 300); return () => clearTimeout(t); /* eslint-disable-line */ }, [q]);

  const pages = data ? Math.ceil(data.total / data.page_size) : 1;

  return (
    <div className="space-y-5" data-testid="super-attractions">
      <h1 className="text-2xl font-bold">Attractions</h1>

      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={15} className="absolute left-3 top-3 text-[#7a7a7a]" />
          <input className="st-input pl-9" placeholder="Search name, slug or owner email" value={q} onChange={(e) => setQ(e.target.value)} data-testid="super-search" />
        </div>
        <Select value={status} onChange={setStatus} testid="super-filter-status" options={[["all", "All statuses"], ["active", "Active"], ["suspended", "Suspended"], ["banned", "Banned"]]} />
        <Select value={stripe} onChange={setStripe} testid="super-filter-stripe" options={[["all", "Any Stripe"], ["connected", "Stripe connected"], ["not_connected", "No Stripe"]]} />
        <Select value={setup} onChange={setSetup} testid="super-filter-setup" options={[["all", "Any setup"], ["incomplete", "Incomplete setup"]]} />
        <Select value={sort} onChange={setSort} testid="super-sort" options={[["newest", "Newest"], ["tickets", "Most tickets"], ["revenue", "Most revenue"], ["alpha", "A–Z"]]} />
      </div>

      <SuperCard className="overflow-hidden">
        <table className="w-full text-sm" data-testid="super-attractions-table">
          <thead className="bg-[#141414] text-[#8a8a8a] text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3">Attraction</th>
              <th className="text-left px-4 py-3">Owner</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Setup</th>
              <th className="text-right px-4 py-3">Tickets</th>
              <th className="text-right px-4 py-3">Gross</th>
              <th className="text-right px-4 py-3">Commission</th>
              <th className="text-right px-4 py-3">Rate</th>
            </tr>
          </thead>
          <tbody>
            {data?.attractions.map((a) => (
              <tr key={a.id} className="border-t border-[#161616] hover:bg-[#121212]">
                <td className="px-4 py-3">
                  <Link to={`/super-admin/attractions/${a.id}`} className="font-medium hover:text-[#dc2626]" data-testid={`super-attraction-row-${a.id}`}>{a.name}</Link>
                  <div className="text-xs text-[#7a7a7a]">/{a.slug}{a.stripe_onboarding_complete ? " · Stripe ✓" : ""}</div>
                </td>
                <td className="px-4 py-3 text-[#bbb]">{a.owner_email || "—"}</td>
                <td className="px-4 py-3"><span className={`text-[11px] px-2 py-0.5 rounded uppercase tracking-wider ${STATUS_BADGE[a.status || "active"]}`}>{a.status || "active"}</span></td>
                <td className="px-4 py-3">
                  {a.setup?.ready_to_sell
                    ? <span className="text-[11px] text-[#86efac]">Ready ✓</span>
                    : <span className="text-[11px] text-[#facc15]" data-testid={`setup-incomplete-${a.id}`}>{a.setup?.required_done}/{a.setup?.required_total} setup</span>}
                </td>
                <td className="px-4 py-3 text-right">{a.tickets_sold}</td>
                <td className="px-4 py-3 text-right">{poundsFromPence(a.gross_pence)}</td>
                <td className="px-4 py-3 text-right">{poundsFromPence(a.commission_pence)}</td>
                <td className="px-4 py-3 text-right">{(a.effective_rate * 100).toFixed(1)}%{a.rate_source !== "default" && <span className="text-[10px] text-[#facc15] ml-1">{a.rate_source}</span>}</td>
              </tr>
            ))}
            {data && data.attractions.length === 0 && <tr><td colSpan={8} className="text-center text-[#7a7a7a] py-10">No attractions found.</td></tr>}
          </tbody>
        </table>
      </SuperCard>

      {pages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button className="st-btn st-btn-ghost text-sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>Prev</button>
          <span className="text-sm text-[#8a8a8a]">Page {page} of {pages}</span>
          <button className="st-btn st-btn-ghost text-sm" disabled={page >= pages} onClick={() => setPage(page + 1)}>Next</button>
        </div>
      )}
    </div>
  );
}

function Select({ value, onChange, options, testid }) {
  return (
    <select className="st-input w-auto" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}>
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );
}
